import React from 'react';

import { Button } from 'shadcn/components/ui/Button';
import { Card } from 'shadcn/components/ui/Card';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Label } from 'shadcn/components/ui/Label';

/**
 * CardListPanel
 *
 * Internal component used by SplitPanelSelector.
 * Renders a scrollable list of selectable cards with header controls.
 *
 * Responsibilities:
 * - Display list of items as cards
 * - Handle item activation (click)
 * - Handle multi-select via checkboxes
 * - Provide select-all / clear-all controls
 *
 * @param {Array} props.items
 * @param {Set} props.selectedIds
 * @param {string|number|null} props.activeItemId
 * @param {Function} props.getKey
 * @param {Function} props.renderCard
 *   Signature: (item, { isSelected, isActive }) => React.Node
 * @param {Function} props.onToggleSelection
 * @param {Function} props.onItemClick
 * @param {Function} props.onSelectAll
 * @param {Function} props.onClearSelection
 * @param {Function} props.onApplyClick
 * @param {string} props.title
 * @param {string} props.actionButtonText
 * @param {string} props.emptyStateMessage
 */
export function CardListPanel({
    actionButtonText,
    activeItemId,
    emptyStateMessage,
    getKey,
    items,
    onApplyClick,
    onClearSelection,
    onItemClick,
    onSelectAll,
    onToggleSelection,
    renderCard,
    selectedIds,
    title,
}) {
    const allSelected = items.length > 0 && selectedIds.size === items.length;

    return (
        <div className="flex flex-col h-full border border-gray-300 rounded-lg bg-white">
            {/* Header with Title, Count, Select All, and Apply Button */}
            <div className="p-4">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                        <span className="text-xs text-gray-900 px-2 py-1 rounded border-1 border-gray-300">
                            {items.length} items
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 px-3 py-2">
                            <Checkbox
                                id="select-all"
                                checked={allSelected}
                                onCheckedChange={(checked) => {
                                    if (checked) {
                                        onSelectAll();
                                    } else {
                                        onClearSelection();
                                    }
                                }}
                            />
                            <Label htmlFor="select-all" className="text-sm font-medium text-gray-900 cursor-pointer">
                                Select All
                            </Label>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2">
                            <Button type="button" onClick={onApplyClick} disabled={selectedIds.size === 0}>
                                {actionButtonText} ({selectedIds.size})
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scrollable Card List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {items.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">{emptyStateMessage}</div>
                ) : (
                    items.map((item) => {
                        const itemId = getKey(item);
                        const isSelected = selectedIds.has(itemId);
                        const isActive = activeItemId === itemId;

                        return (
                            <Card
                                key={itemId}
                                className={`relative transition-colors overflow-hidden p-0 ${
                                    isActive ? 'border-blue-900' : 'border-gray-200'
                                }`}
                                onClick={() => onItemClick(itemId)}
                            >
                                {/* Card Content - fills entire card */}
                                {renderCard ? renderCard(item, { isActive, isSelected }) : <DefaultCard item={item} />}

                                {/* Checkbox - positioned absolutely on top so card content background color covers entire card */}
                                <div className="absolute top-4 left-4 z-10">
                                    <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={(checked, e) => {
                                            e?.stopPropagation();
                                            onToggleSelection(itemId);
                                        }}
                                    />
                                </div>
                            </Card>
                        );
                    })
                )}
            </div>
        </div>
    );
}

/**
 * DefaultCard - Fallback card renderer used when no custom renderCard function is provided.
 *
 * Displays a simple card layout with:
 * - Primary text from item.name, item.title, or "Untitled"
 * - Optional description text from item.description
 *
 * @param {Object} props
 * @param {Object} props.item - The item object to render
 * @param {string} [props.item.name] - Primary display text (first priority)
 * @param {string} [props.item.title] - Primary display text (second priority, if name not present)
 * @param {string} [props.item.description] - Optional secondary text displayed below the title
 */
function DefaultCard({ item }) {
    return (
        <div>
            <div className="font-medium text-gray-900">{item.name || item.title || 'Untitled'}</div>
            {item.description && <div className="text-sm text-gray-600 mt-1">{item.description}</div>}
        </div>
    );
}
